import React, { useState, useEffect, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { InventoryItemForm } from "./InventoryTable";

type QuantityInputProps = {
  item: InventoryItemForm;
  initialQuantity: number;
  onChange: (item: InventoryItemForm, quantity: number) => void;
};

const QuantityInput = ({
  item,
  initialQuantity,
  onChange,
}: QuantityInputProps) => {
  const [quantity, setQuantity] = useState(initialQuantity);

  // Update internal state when initialQuantity prop changes
  useEffect(() => {
    setQuantity(initialQuantity);
  }, [initialQuantity]);

  // Handle quantity increment
  const incrementQuantity = useCallback(() => {
    const newQuantity = quantity + 1;
    setQuantity(newQuantity);
    onChange(item, newQuantity);
  }, [quantity, item, onChange]);

  // Handle quantity decrement
  const decrementQuantity = useCallback(() => {
    if (quantity > 0) {
      const newQuantity = quantity - 1;
      setQuantity(newQuantity);
      onChange(item, newQuantity);
    }
  }, [quantity, item, onChange]);

  // Handle direct input change
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newQuantity = parseInt(e.target.value || "0", 10);
      setQuantity(newQuantity);
      onChange(item, newQuantity);
    },
    [item, onChange]
  );

  console.debug(
    `Rendering QuantityInput for: ${item.name} (${item.uuid}) with quantity: ${quantity}`
  );

  return (
    <div className="flex items-center space-x-0.5">
      <Button
        type="button"
        variant="outline"
        onClick={decrementQuantity}
        disabled={quantity <= 0}
      >
        -
      </Button>
      <Input
        type="text"
        value={quantity}
        onChange={handleInputChange}
        className="w-16 text-center"
        min="0"
        onFocus={(e) => e.target.select()}
      />
      <Button type="button" variant="outline" onClick={incrementQuantity}>
        +
      </Button>
    </div>
  );
};

// Apply React.memo with custom comparison function
export default React.memo(QuantityInput, (prevProps, nextProps) => {
  // Return true if props are equal (skip re-render)
  return (
    prevProps.item.uuid === nextProps.item.uuid &&
    prevProps.initialQuantity === nextProps.initialQuantity
    // We don't compare onChange function because it should be memoized by the parent
  );
});
